/**
 * RevenueReports — 매출 리포트 (전용 페이지)
 *
 * 상단: "리포트 생성" 버튼 → 모달에서 기간 선택 후 생성.
 * 하단: 저장된 스냅샷 히스토리 테이블 (기간/생성일시/순매출/결제건수/AI해석/PDF + 액션).
 * 데이터 소스:
 *   - 목록: GET  /api/stats/revenue-report/history
 *   - 열람: GET  /api/stats/revenue-report?from&to[&force=1]   (스냅샷 재사용, 없으면 생성)
 *   - PDF:  GET  /api/stats/revenue-report/pdf?from&to (lazy 렌더 후 다운로드)
 *   - 발송: POST /api/stats/revenue-report/send
 * 리포트는 결제일(KST) 기준 유입경로 분석 + AI 해석 포함 (lib/reports/report-snapshot.ts).
 */

import { useMemo, useState } from 'react'
import { FileText, Download, Mail, ExternalLink } from 'lucide-react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useToast } from '@/components/ToastContainer'
import {
  Button,
  Modal,
  DateRangePicker,
  Textarea,
  Badge,
  Alert,
  Table,
  formatCurrency,
  formatNumber,
  formatDate,
  type TableColumn,
} from '@/ui'
import { SHARED_RANGE_PRESETS, lastNDays } from '@/lib/dateRange'
import styles from './RevenueReports.module.css'

interface HistoryItem {
  from: string
  to: string
  generatedAt: string
  netRevenue: number
  paymentsCount: number
  aiIncluded: boolean
  pdfReady: boolean
  generatedBy: string | null
}

interface HistoryResponse {
  total: number
  limit: number
  offset: number
  items: HistoryItem[]
}

/** 생성 주체 라벨 — 'cron' | 'admin:<id>' → 사람이 읽는 문구 */
function generatedByLabel(by: string | null): string {
  if (!by) return '—'
  if (by === 'cron') return '자동(주간)'
  if (by.startsWith('admin:')) return '수동'
  return by
}

export function RevenueReports() {
  const { addToast } = useToast()

  const { data, isLoading, error, refetch } = useApiFetch<HistoryResponse>(
    '/api/stats/revenue-report/history?limit=50',
  )

  const { trigger: triggerSend, isLoading: sending } = useApiMutation<
    { sent: number; failed: number; recipients: number },
    { from: string; to: string; emails?: string[] }
  >('/api/stats/revenue-report/send', 'POST')

  // ── 생성 모달 상태 (휘발성 UI — useState) ──
  const [modalOpen, setModalOpen] = useState(false)
  const [range, setRange] = useState<{ from: string; to: string; preset: string | null }>(() => {
    const { from, to } = lastNDays(7)
    return { from, to, preset: '1w' }
  })

  // ── 발송 모달 상태 ──
  const [sendModal, setSendModal] = useState<HistoryItem | null>(null)
  const [emailInput, setEmailInput] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)

  // 선택한 기간이 이미 히스토리에 있는지 (중복 감지)
  const existing = useMemo(
    () => data?.items.find((it) => it.from === range.from && it.to === range.to) ?? null,
    [data, range.from, range.to],
  )

  const handleRangeChange = (
    r: { from: string | null; to: string | null },
    meta: { presetKey: string | null },
  ) => {
    if (r.from && r.to) setRange({ from: r.from, to: r.to, preset: meta.presetKey })
  }

  // 스냅샷 생성 겸 열람 — GET /revenue-report 가 없으면 build+persist 후 HTML 반환.
  // force=true 면 기존 스냅샷 무시하고 재집계(Bedrock 재호출).
  const openReport = (force = false) => {
    const qs = `from=${range.from}&to=${range.to}${force ? '&force=1' : ''}`
    window.open(`/api/stats/revenue-report?${qs}`, '_blank', 'noopener')
    setModalOpen(false)
    addToast({
      type: 'info',
      message: force
        ? '리포트를 재생성합니다(AI 재분석). 잠시 후 목록이 갱신됩니다.'
        : '리포트를 생성/열람합니다. 잠시 후 목록이 갱신됩니다.',
    })
    setTimeout(refetch, 4000)
  }

  const openHtml = (row: { from: string; to: string }) => {
    window.open(`/api/stats/revenue-report?from=${row.from}&to=${row.to}`, '_blank', 'noopener')
  }

  const downloadPdf = (row: { from: string; to: string }) => {
    window.open(`/api/stats/revenue-report/pdf?from=${row.from}&to=${row.to}`, '_blank', 'noopener')
  }

  // ✉ 버튼 → 발송 모달 오픈 (기간 컨텍스트 보관 + 입력 초기화)
  const openSendModal = (row: HistoryItem) => {
    setEmailInput('')
    setEmailError(null)
    setSendModal(row)
  }

  // 입력값(쉼표/줄바꿈/공백 구분)을 이메일 배열로 파싱
  const parseEmails = (raw: string): string[] =>
    raw
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter(Boolean)

  const submitSend = async () => {
    if (!sendModal) return
    const emails = parseEmails(emailInput)
    // 직접 입력한 경우만 형식 검증. 비우면 어드민 설정 기본 수신자 사용.
    if (emails.length > 0) {
      const invalid = emails.filter((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
      if (invalid.length > 0) {
        setEmailError(`올바르지 않은 이메일: ${invalid.join(', ')}`)
        return
      }
    }
    setEmailError(null)
    const result = await triggerSend({
      from: sendModal.from,
      to: sendModal.to,
      ...(emails.length > 0 ? { emails } : {}),
    })
    if (result) {
      setSendModal(null)
      addToast({
        type: result.failed > 0 ? 'error' : 'success',
        message:
          result.recipients === 0
            ? '수신자가 없습니다 — 직접 입력하거나 어드민 설정에서 수신자를 등록하세요'
            : `발송 완료 — ${result.sent}/${result.recipients}건 성공${result.failed > 0 ? `, ${result.failed}건 실패` : ''}`,
      })
    } else {
      addToast({ type: 'error', message: '리포트 발송에 실패했습니다' })
    }
  }

  const columns: TableColumn<HistoryItem>[] = [
    { key: 'period', label: '기간', render: (r) => `${r.from} ~ ${r.to}`, width: '190px' },
    {
      key: 'generatedAt',
      label: '생성일시',
      render: (r) => formatDate(r.generatedAt, 'yyyy-MM-dd HH:mm'),
      width: '150px',
    },
    {
      key: 'netRevenue',
      label: '순매출',
      render: (r) => `${formatCurrency(r.netRevenue)}원`,
      align: 'right',
      width: '120px',
    },
    {
      key: 'paymentsCount',
      label: '결제',
      render: (r) => `${formatNumber(r.paymentsCount)}건`,
      align: 'right',
      width: '80px',
    },
    {
      key: 'generatedBy',
      label: '생성',
      render: (r) => generatedByLabel(r.generatedBy),
      width: '90px',
    },
    {
      key: 'aiIncluded',
      label: 'AI 해석',
      render: (r) => (r.aiIncluded ? <Badge color="green">포함</Badge> : <Badge color="gray">없음</Badge>),
      width: '90px',
    },
    {
      key: 'pdfReady',
      label: 'PDF',
      render: (r) => (r.pdfReady ? <Badge color="blue">준비됨</Badge> : <Badge color="gray">미생성</Badge>),
      width: '90px',
    },
    {
      key: 'actions',
      label: '',
      render: (r) => (
        <div className={styles.actions}>
          <Button size="sm" variant="ghost" onClick={() => openHtml(r)} title="HTML 보기">
            <ExternalLink size={14} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => downloadPdf(r)} title="PDF 다운로드">
            <Download size={14} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => openSendModal(r)} title="이메일 발송">
            <Mail size={14} />
          </Button>
        </div>
      ),
      width: '130px',
    },
  ]

  return (
    <div className={styles.root}>
      {/* 상단 — 새 리포트 생성 */}
      <div className={styles.header}>
        <p className={styles.hint}>
          결제일(KST) 기준 매출 유입경로 리포트를 기간별로 생성·저장합니다. 같은 기간은 저장된 스냅샷을
          재사용하며(AI 재분석 없음), 아래 목록에서 열람·PDF 다운로드·이메일 발송할 수 있습니다.
        </p>
        <Button size="sm" onClick={() => setModalOpen(true)}>
          <FileText size={14} />
          리포트 생성
        </Button>
      </div>

      {error && <Alert variant="danger">목록을 불러오지 못했습니다: {error}</Alert>}

      <Table<HistoryItem>
        columns={columns}
        data={data?.items ?? []}
        rowKey={(r) => `${r.from}_${r.to}`}
        isLoading={isLoading}
        emptyMessage="아직 생성된 리포트가 없습니다. 우측 상단에서 리포트를 생성하세요."
        size="sm"
      />

      {/* 생성 모달 */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="매출 리포트 생성" size="md">
        <div className={styles.modalBody}>
          <label className={styles.modalLabel}>기간 선택 (결제일 KST 기준)</label>
          <DateRangePicker
            size="sm"
            value={{ from: range.from, to: range.to }}
            activePresetKey={range.preset}
            presets={SHARED_RANGE_PRESETS}
            onChange={handleRangeChange}
          />

          {existing ? (
            <>
              <Alert variant="warning" title="이미 생성된 리포트가 있습니다">
                {range.from} ~ {range.to} 기간은 {formatDate(existing.generatedAt, 'yyyy-MM-dd HH:mm')}에
                생성됐습니다. 기존 리포트를 열거나, 최신 데이터로 다시 분석(AI 재호출)할 수 있습니다.
              </Alert>
              <div className={styles.modalActions}>
                <Button size="sm" variant="secondary" onClick={() => openHtml(range)}>
                  기존 리포트 열기
                </Button>
                <Button size="sm" variant="primary" onClick={() => openReport(true)}>
                  다시 생성 (재분석)
                </Button>
              </div>
            </>
          ) : (
            <div className={styles.modalActions}>
              <Button size="sm" variant="ghost" onClick={() => setModalOpen(false)}>
                취소
              </Button>
              <Button size="sm" variant="primary" onClick={() => openReport(false)}>
                <FileText size={14} />
                생성
              </Button>
            </div>
          )}
        </div>
      </Modal>

      {/* 발송 모달 — 받는 사람 직접 입력 (비우면 어드민 설정 기본 수신자) */}
      <Modal
        open={sendModal !== null}
        onClose={() => setSendModal(null)}
        title="리포트 이메일 발송"
        size="md"
      >
        {sendModal && (
          <div className={styles.modalBody}>
            <p className={styles.hint}>
              {sendModal.from} ~ {sendModal.to} 리포트를 이메일로 발송합니다. PDF 다운로드 링크(7일 유효)가
              본문에 포함됩니다.
            </p>
            <Textarea
              size="sm"
              label="받는 사람 (비우면 어드민 설정 수신자로 발송)"
              placeholder="쉼표·줄바꿈으로 여러 명 입력 — 예: a@fiket.ai, b@fiket.ai"
              rows={3}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              error={emailError ?? undefined}
            />
            <div className={styles.modalActions}>
              <Button size="sm" variant="ghost" onClick={() => setSendModal(null)}>
                취소
              </Button>
              <Button size="sm" variant="primary" loading={sending} onClick={submitSend}>
                <Mail size={14} />
                발송
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
